import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,600;1,9..144,400;1,9..144,600&family=Nunito:wght@400;500;600;700;800&display=swap');
@keyframes fadeUp { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:translateY(0); } }
@keyframes float { 0%,100%{transform:translateY(0px) rotate(-2deg)} 50%{transform:translateY(-10px) rotate(2deg)} }
@keyframes wiggle { 0%,100%{transform:rotate(-3deg)} 50%{transform:rotate(3deg)} }
@keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
@keyframes blob { 0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%} 50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%} }
* { box-sizing:border-box; margin:0; padding:0; }
input,textarea,button,select { font-family:'Nunito',sans-serif; }
input:focus,textarea:focus { outline:none; }
`;

const CREAM = '#FDFAF5';
const WARM = '#F5EFE0';
const PINK = '#F4A7B9';
const PINK2 = '#E8738A';
const BROWN = '#4A3728';
const BROWN2 = '#7C5C4A';
const SAGE = '#A8C5A0';
const SAND = '#E8D5B7';
const BLUE = '#A8C8E8';

function useInView(t = 0.1) {
  const ref = useRef(null);
  const [v, set] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) set(true); }, { threshold: t });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, [t]);
  return [ref, v];
}

function Up({ children, d = 0 }) {
  const [ref, v] = useInView();
  return (
    <div ref={ref} style={{ opacity: v?1:0, transform: v?'none':'translateY(26px)', transition: `opacity .7s ease ${d}s, transform .7s ease ${d}s` }}>
      {children}
    </div>
  );
}

function Blob({ color, size, style }) {
  return (
    <div style={{
      width: size, height: size,
      background: color,
      animation: 'blob 8s ease-in-out infinite',
      position: 'absolute',
      opacity: 0.35,
      ...style
    }} />
  );
}

const SERVICES = [
  { emoji: '🌾', title: 'Gentle Grain', desc: 'Soft, taste-safe grains that build fine motor skills through scooping, pouring, and sorting.', color: '#F5E6C8' },
  { emoji: '☁️', title: 'Whipped Cloud', desc: 'Fluffy sensory foam that melts in little hands — perfect for tactile exploration and calming play.', color: '#E8F0FA' },
  { emoji: '🩷', title: 'Velour Dough', desc: 'Silky-smooth dough that strengthens hand muscles and sparks open-ended creative play.', color: '#FAE8F0' },
  { emoji: '🌱', title: 'Soft Soil', desc: 'Nature-inspired bins with safe "soil" and hidden treasures — encourages problem solving and curiosity.', color: '#E8F5E8' },
];

const BENEFITS = [
  { icon: '🤲', title: 'Fine Motor Skills', desc: 'Scooping, pinching, pouring — every activity builds hand strength and coordination.' },
  { icon: '🧠', title: 'Brain Development', desc: 'Sensory play creates neural pathways that support learning, memory, and problem solving.' },
  { icon: '😌', title: 'Calm & Focus', desc: 'Structured play stations designed to invite calm, sustained engagement.' },
  { icon: '🔍', title: 'Curiosity & Independence', desc: 'Open-ended materials let children lead their own discovery without instruction.' },
  { icon: '💬', title: 'Sensory Processing', desc: 'Supports children who experience sensory defensiveness, especially in early feeding development.' },
  { icon: '🏫', title: 'Any Space', desc: 'Homes, libraries, schools, daycares, community centers — we come to you.' },
];

const PROCESS = [
  { n: '01', title: 'Consultation & Scheduling', desc: 'We chat about your space, the age group, and your goals. Then we find a date that works.' },
  { n: '02', title: 'Custom Curation', desc: 'Sensory bins and materials are hand-selected to match your children\'s developmental needs.' },
  { n: '03', title: 'Setup & Experience', desc: 'We arrive, set everything up, and create an engaging, beautiful play environment.' },
  { n: '04', title: 'Breakdown & Sanitation', desc: 'When playtime ends, we remove, clean, and sanitize every single item. You do nothing.' },
];

export default function MiniMindsLanding() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name:'', space:'', phone:'', email:'', notes:'' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const sf = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const go = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  async function handleSubmit() {
    if (!form.name || !form.email) return;
    setSubmitting(true);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/demo-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, business: form.space, phone: form.phone, email: form.email, current_software: form.notes, source: 'miniminds_landing' })
      });
      setSubmitted(true);
    } catch(e) {
      console.error(e);
    }
    setSubmitting(false);
  }

  const inp = {
    width: '100%', padding: '12px 16px',
    background: '#fff',
    border: `2px solid ${SAND}`,
    borderRadius: 12, color: BROWN, fontSize: 14,
    fontFamily: 'Nunito, sans-serif',
    transition: 'border-color .2s'
  };

  return (
    <div style={{ background: CREAM, color: BROWN, fontFamily: 'Nunito, sans-serif', overflowX: 'hidden' }}>
      <style>{FONTS}</style>

      {/* NAV */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 99, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 40px', height: 64, background: 'rgba(253,250,245,0.97)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${SAND}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: PINK, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🧠</div>
          <div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, fontWeight: 600, color: BROWN, lineHeight: 1 }}>Mini Minds</div>
            <div style={{ fontSize: 9, color: BROWN2, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Play Co.</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          {[['Services','services'],['How It Works','process'],['Book','book']].map(([l,id]) => (
            <span key={id} onClick={() => go(id)} style={{ fontSize: 13, color: BROWN2, cursor: 'pointer', fontWeight: 600 }}>{l}</span>
          ))}
          <button onClick={() => go('book')} style={{ background: PINK2, color: '#fff', border: 'none', padding: '9px 20px', borderRadius: 100, fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: `0 4px 14px rgba(232,115,138,0.35)` }}>
            Book a Session
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ minHeight: '90vh', display: 'flex', alignItems: 'center', padding: '80px 40px 60px', position: 'relative', overflow: 'hidden' }}>
        <Blob color={PINK} size={500} style={{ top: '-10%', right: '-8%' }} />
        <Blob color={SAGE} size={350} style={{ bottom: '5%', right: '20%', animationDelay: '3s' }} />
        <Blob color={SAND} size={280} style={{ top: '30%', right: '40%', animationDelay: '5s' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: 680 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(244,167,185,0.2)', border: `1px solid rgba(244,167,185,0.5)`, borderRadius: 100, padding: '6px 16px', marginBottom: 28, animation: 'fadeUp .5s ease both' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: SAGE, boxShadow: `0 0 8px ${SAGE}`, animation: 'pulse 2s infinite' }} />
            <span style={{ fontSize: 11, color: BROWN2, fontWeight: 700, letterSpacing: '0.06em' }}>Now booking Spring 2026 sessions</span>
          </div>

          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(42px, 6vw, 72px)', fontWeight: 400, lineHeight: 1.1, marginBottom: 24, animation: 'fadeUp .6s ease .1s both' }}>
            Sensory play that<br />
            <em style={{ color: PINK2, fontStyle: 'italic' }}>actually develops</em><br />
            your child.
          </h1>

          <p style={{ fontSize: 16, lineHeight: 1.7, color: BROWN2, maxWidth: 500, marginBottom: 36, animation: 'fadeUp .6s ease .2s both' }}>
            We bring thoughtfully curated sensory bin experiences to your home, school, or community space — designed by developmental science, cleaned and sanitized after every session.
          </p>

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', animation: 'fadeUp .6s ease .3s both' }}>
            <button onClick={() => go('book')} style={{ background: PINK2, color: '#fff', border: 'none', padding: '14px 32px', borderRadius: 100, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: `0 6px 20px rgba(232,115,138,0.4)` }}>
              Schedule a session →
            </button>
            <button onClick={() => go('services')} style={{ background: 'transparent', color: BROWN, border: `2px solid ${SAND}`, padding: '14px 28px', borderRadius: 100, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              See what we bring
            </button>
          </div>

          <div style={{ display: 'flex', gap: 32, marginTop: 48, animation: 'fadeUp .6s ease .4s both' }}>
            {[['🏠','Homes'],['📚','Libraries'],['🏫','Schools'],['🏘️','Community Spaces']].map(([e,l]) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: BROWN2, fontWeight: 600 }}>
                <span>{e}</span><span>{l}</span>
              </div>
            ))}
          </div>
        </div>

        {/* floating brain mascot area */}
        <div style={{ position: 'absolute', right: '6%', top: '50%', transform: 'translateY(-50%)', animation: 'float 6s ease-in-out infinite', zIndex: 1 }}>
          <img src="/Mini_Minds_Transparent__1_.png" alt="Mini Minds Play Co." style={{ width: 320, height: 'auto', filter: 'drop-shadow(0 20px 40px rgba(92,61,46,0.15))' }} />
        </div>
      </section>

      {/* MARQUEE */}
      <div style={{ background: PINK2, padding: '14px 0', overflow: 'hidden' }}>
        <div style={{ display: 'flex', animation: 'marquee 25s linear infinite', width: 'max-content' }}>
          {Array(4).fill(['✨ Taste-safe materials', '🧡 Age-appropriate', '🌿 Sensory play', '🎓 Science-backed', '💛 Hands-on learning', '🏡 We come to you', '🧼 Fully sanitized', '❤️ Made with love']).flat().map((t,i) => (
            <span key={i} style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: '0.12em', textTransform: 'uppercase', padding: '0 28px', whiteSpace: 'nowrap' }}>{t}</span>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section id="services" style={{ padding: '100px 40px', background: WARM }}>
        <Up>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: PINK2, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 14 }}>What We Bring</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 400, color: BROWN }}>
              Four sensory experiences.<br /><em style={{ color: PINK2 }}>Infinite discovery.</em>
            </h2>
          </div>
        </Up>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 24, maxWidth: 1100, margin: '0 auto' }}>
          {SERVICES.map((s, i) => (
            <Up key={s.title} d={i * 0.1}>
              <div style={{ background: s.color, borderRadius: 24, padding: '40px 28px', textAlign: 'center', border: `1px solid rgba(0,0,0,0.05)` }}>
                <div style={{ fontSize: 48, marginBottom: 20, animation: 'wiggle 3s ease-in-out infinite', animationDelay: `${i*0.5}s`, display: 'inline-block' }}>{s.emoji}</div>
                <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 600, color: BROWN, marginBottom: 12 }}>{s.title}</h3>
                <p style={{ fontSize: 13, color: BROWN2, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            </Up>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section style={{ padding: '100px 40px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
          <Up>
            <div style={{ fontSize: 11, fontWeight: 700, color: PINK2, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 14 }}>Designed by Play, Backed by Science</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(28px, 3.5vw, 44px)', fontWeight: 400, color: BROWN, marginBottom: 20, lineHeight: 1.2 }}>
              Every activity has<br /><em style={{ color: PINK2 }}>a developmental purpose.</em>
            </h2>
            <p style={{ fontSize: 14, color: BROWN2, lineHeight: 1.7, marginBottom: 28 }}>
              We don't just dump toys in a bin. Every material, every texture, every activity is chosen to support a specific developmental milestone — from fine motor control to sensory processing to early logic skills.
            </p>
            <button onClick={() => go('book')} style={{ background: BROWN, color: '#fff', border: 'none', padding: '12px 28px', borderRadius: 100, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Learn more →
            </button>
          </Up>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {BENEFITS.map((b, i) => (
              <Up key={b.title} d={i * 0.08}>
                <div style={{ background: WARM, borderRadius: 16, padding: '20px', border: `1px solid ${SAND}` }}>
                  <div style={{ fontSize: 26, marginBottom: 10 }}>{b.icon}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: BROWN, marginBottom: 6 }}>{b.title}</div>
                  <div style={{ fontSize: 11, color: BROWN2, lineHeight: 1.5 }}>{b.desc}</div>
                </div>
              </Up>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" style={{ padding: '100px 40px', background: WARM }}>
        <Up>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: PINK2, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 14 }}>How It Works</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 400, color: BROWN }}>
              Simple from start to <em style={{ color: PINK2 }}>sparkle.</em>
            </h2>
          </div>
        </Up>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32 }}>
          {PROCESS.map((s, i) => (
            <Up key={s.title} d={i * 0.12}>
              <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                <div style={{ width: 52, height: 52, borderRadius: '50%', background: PINK, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontFamily: 'Fraunces, serif', fontSize: 18, fontWeight: 600, color: BROWN }}>
                  {s.n}
                </div>
                <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 17, fontWeight: 600, color: BROWN, marginBottom: 10 }}>{s.title}</h3>
                <p style={{ fontSize: 12, color: BROWN2, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            </Up>
          ))}
        </div>
      </section>

      {/* BOOKING FORM */}
      <section id="book" style={{ padding: '100px 40px', position: 'relative', overflow: 'hidden' }}>
        <Blob color={PINK} size={400} style={{ top: '-10%', right: '-5%' }} />
        <Blob color={SAGE} size={300} style={{ bottom: '-5%', left: '-5%', animationDelay: '4s' }} />

        <div style={{ maxWidth: 600, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Up>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: PINK2, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 14 }}>Book a Session</div>
              <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 400, color: BROWN, marginBottom: 16 }}>
                Bring the joy of<br /><em style={{ color: PINK2 }}>sensory play to your space.</em>
              </h2>
              <p style={{ fontSize: 14, color: BROWN2, lineHeight: 1.6 }}>
                Fill out the form below and we'll reach out to schedule your consultation. No pressure, no commitment.
              </p>
            </div>
          </Up>

          {submitted ? (
            <Up>
              <div style={{ background: '#fff', borderRadius: 24, padding: '52px 40px', textAlign: 'center', border: `2px solid ${PINK}`, boxShadow: `0 20px 60px rgba(244,167,185,0.25)` }}>
                <div style={{ fontSize: 64, marginBottom: 20 }}>🎉</div>
                <h3 style={{ fontFamily: 'Fraunces, serif', fontSize: 28, fontWeight: 600, color: BROWN, marginBottom: 12 }}>You're on the list!</h3>
                <p style={{ fontSize: 14, color: BROWN2, lineHeight: 1.6 }}>We'll reach out within 24 hours to discuss your space and schedule your session.</p>
              </div>
            </Up>
          ) : (
            <Up d={0.1}>
              <div style={{ background: '#fff', borderRadius: 24, padding: '40px', border: `1px solid ${SAND}`, boxShadow: `0 20px 60px rgba(74,55,40,0.08)` }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: BROWN, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Your Name *</label>
                    <input style={inp} placeholder="Jane Smith" value={form.name} onChange={sf('name')} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: BROWN, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Phone</label>
                    <input style={inp} placeholder="(555) 000-0000" value={form.phone} onChange={sf('phone')} />
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: BROWN, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Email *</label>
                  <input style={inp} placeholder="you@email.com" value={form.email} onChange={sf('email')} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: BROWN, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Your Space</label>
                  <select style={{...inp, appearance:'none'}} value={form.space} onChange={sf('space')}>
                    <option value="">Select a space type...</option>
                    <option>Home</option>
                    <option>Library</option>
                    <option>School / Daycare</option>
                    <option>Community Center</option>
                    <option>Other</option>
                  </select>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: BROWN, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Age Group & Notes</label>
                  <textarea style={{...inp, resize:'vertical', minHeight:90}} placeholder="e.g. 2–4 year olds, group of 8 kids, interested in Saturday sessions..." value={form.notes} onChange={sf('notes')} />
                </div>
                <div style={{ fontSize: 10, color: BROWN2, marginBottom: 16, lineHeight: 1.5 }}>
                  By submitting, you agree to receive SMS notifications. Reply STOP to opt out. See our <a href="/privacy" style={{ color: PINK2 }}>Privacy Policy</a> and <a href="/terms" style={{ color: PINK2 }}>Terms</a>.
                </div>
                <button onClick={handleSubmit} disabled={submitting || !form.name || !form.email} style={{ width: '100%', background: PINK2, color: '#fff', border: 'none', padding: '16px', borderRadius: 100, fontSize: 15, fontWeight: 700, cursor: 'pointer', boxShadow: `0 6px 20px rgba(232,115,138,0.4)`, opacity: (!form.name || !form.email) ? 0.5 : 1 }}>
                  {submitting ? 'Sending...' : 'Schedule my session →'}
                </button>
              </div>
            </Up>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '48px 40px', background: BROWN, color: 'rgba(255,255,255,0.6)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
        <div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 20, color: '#fff', marginBottom: 4 }}>Mini Minds Play Co.</div>
          <div style={{ fontSize: 12 }}>Supporting early childhood development through sensory play</div>
        </div>
        <div style={{ fontSize: 12, textAlign: 'right' }}>
          <div style={{ marginBottom: 6, color: '#fff' }}>(909) 367-8763 · minimindspco@gmail.com</div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
            <a href="/privacy" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Terms</a>
          </div>
          <div style={{ marginTop: 8, fontSize: 11 }}>Powered by IVision CRM</div>
        </div>
      </footer>
    </div>
  );
}
